import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { GLOBAL_IMAGES } from "../../assets/asset";
import { useSubscription } from "../../context/SubscriptionContext";
import {
  formatSubscriptionDate,
  getEffectiveSubscriptionStatus,
  getPlanExpiryDate,
  getSubscriptionIntervalKey,
} from "../../utils/subscriptionDates";

const getPlanType = (subscription = {}) => {
  const parts = [
    subscription?.plan_type,
    subscription?.planType,
    subscription?.plan_name,
    subscription?.subscription_plan,
    subscription?.plan,
    subscription?.plan_key,
    subscription?.price_plan,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  if (parts.includes("pro")) return "Pro";
  if (parts.includes("basic")) return "Basic";
  return "N/A";
};

const formatPlanInterval = (value = "") => {
  if (!value) return "";
  const normalized = String(value).trim().toLowerCase();
  if (normalized === "monthly") return "Monthly";
  if (normalized === "yearly") return "Yearly";
  return "";
};

const DetailRow = ({ label, value }) => (
  <div className="billing-detail-row flex items-center justify-between gap-3 border-b border-[#d2d8df] py-2.5 md:py-3">
    <p className="billing-detail-label text-[#2e3a4e] text-sm md:text-base font-semibold">{label}</p>
    <p className="billing-detail-value text-[#0B2B66] text-sm md:text-base font-bold text-right capitalize">
      {value}
    </p>
  </div>
);

const BillingDetailsPage = () => {
  const navigate = useNavigate();
  const { subscription, isChecking, checkSubscription, checkError } =
    useSubscription();

  useEffect(() => {
    checkSubscription().catch(() => null);
  }, [checkSubscription]);

  const status = getEffectiveSubscriptionStatus(subscription);
  const planType = getPlanType(subscription);
  const requiresPaymentMethod = subscription?.requires_payment_method
    ? "Yes"
    : "No";
  const daysLeftNumber = Number(subscription?.days_left ?? 0);
  const daysLeft = String(daysLeftNumber);
  const interval = getSubscriptionIntervalKey(subscription);
  const formattedInterval = formatPlanInterval(interval);
  const planTypeDisplay =
    planType === "N/A"
      ? formattedInterval || "N/A"
      : formattedInterval
        ? `${planType} ${formattedInterval}`
        : planType;
  const computedPlanExpiryDate = getPlanExpiryDate(subscription);

  return (
    <div
      className="billing-details-page min-h-screen flex flex-col bg-blue-50 font-mali"
      style={{
        backgroundImage: `url(${GLOBAL_IMAGES.onboarding_bg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <header className="billing-details-header relative w-full flex items-center justify-center px-4 pt-3 md:pt-4">
        <div className="billing-details-header-left absolute left-4 md:left-8 flex items-center gap-2 md:gap-4">
          <img src={GLOBAL_IMAGES.dingo} className="billing-details-logo h-20 w-auto" alt="Logo" />
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="billing-details-back h-14 w-14 flex items-center justify-center"
            aria-label="Go back"
          >
            <img
              src={GLOBAL_IMAGES.LoginBackButton}
              className="billing-details-back-img h-14 w-14"
              alt="Back"
            />
          </button>
        </div>
        <h1 className="billing-details-title text-xl md:text-3xl font-bold text-black tracking-tight">
          Billing Details
        </h1>
      </header>

      <div className="billing-details-content w-[86%] md:w-[60%] lg:w-[50%] mr-auto ml-auto mt-7 md:mt-8 mb-8">
        <div className="billing-details-card bg-[#e8eef2]/75 backdrop-blur-[1px] rounded-2xl shadow-[0_8px_24px_rgba(17,31,52,0.15)] p-4 md:p-5 border border-white/50">
          <h2 className="billing-details-card-title text-[#122f6e] text-lg md:text-2xl font-extrabold mb-1.5">
            Subscription Billing Information
          </h2>
          <p className="billing-details-card-copy text-[#4b586b] text-xs md:text-sm font-medium mb-4 md:mb-6">
            View your current billing and subscription status.
          </p>

          {isChecking ? (
            <p className="billing-details-loading text-[#4b586b] text-xs md:text-sm font-semibold">
              Loading billing details...
            </p>
          ) : (
            <>
              <DetailRow label="Subscription Status" value={status} />
              <DetailRow label="Plan Type" value={planTypeDisplay} />
              <DetailRow
                label="Requires Payment Method"
                value={requiresPaymentMethod}
              />
              <DetailRow label="Trial Days Left" value={daysLeft} />
              <DetailRow
                label="Trial End Date"
                value={formatSubscriptionDate(subscription?.trial_end_date)}
              />
              <DetailRow
                label="Plan Expiry Date"
                value={formatSubscriptionDate(computedPlanExpiryDate)}
              />
            </>
          )}

          {checkError ? (
            <p className="billing-details-error text-red-600 mt-3 text-xs md:text-sm font-semibold">
              {checkError}
            </p>
          ) : null}

          <div className="billing-details-actions flex flex-col md:flex-row gap-2.5 md:gap-3 mt-5 md:mt-6">
            <button
              type="button"
              onClick={() => navigate("/subscription")}
              className="billing-details-action w-full md:w-auto px-4 py-2 rounded-lg bg-[#0B2B66] text-white text-sm md:text-sm font-bold border-2 border-[#0B2B66] hover:bg-[#112b5c] transition"
            >
              Go to Payment
            </button>
            <button
              type="button"
              onClick={() => navigate("/subscription-terms")}
              className="billing-details-action w-full md:w-auto px-4 py-2 rounded-lg border-2 border-[#0B2B66] bg-[#f4efe2] text-[#0B2B66] text-sm md:text-sm font-bold hover:bg-[#f8f2e7] transition"
            >
              Read Subscription Terms
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BillingDetailsPage;
